import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Signal,
  viewChild
} from '@angular/core';

import { filter, switchMap, tap } from 'rxjs/operators';

import {
  TiimeButtonModule,
  TiimeIconModule,
  TiimeSnackbarService,
  TiimeSplitButtonModule,
  TiimeTooltipModule
} from 'tiime-material';

import { DocumentApiService } from '@api-services/document-api.service';
import { DateHelper } from '@helpers/date.helper';
import { FileHelper } from '@helpers/file.helper';
import { Document } from '@models/document';
import { PieceFile } from '@models/piece-file';
import { AddFileFromDocumentsDialogModule } from '@modules/shared/dialogs/add-file-from-documents-dialog/add-file-from-documents-dialog.module';
import { AddFileFromDocumentsDialogService } from '@modules/shared/dialogs/add-file-from-documents-dialog/add-file-from-documents-dialog.service';
import { UPLOAD_FILE_CONFIG } from '@modules/shared/overlays/upload-file-overlay/upload-file-config.constant';

@Component({
  selector: 'app-add-files-button',
  standalone: true,
  imports: [
    AddFileFromDocumentsDialogModule,
    TiimeButtonModule,
    CommonModule,
    TiimeSplitButtonModule,
    TiimeTooltipModule,
    TiimeIconModule
  ],
  templateUrl: './add-files-button.component.html',
  styleUrls: ['./add-files-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFilesButtonComponent {
  readonly fileInput: Signal<ElementRef> = viewChild.required<ElementRef>('fileInput');

  @Input() showAddFromDocuments = false;

  @Input('acceptedTypes') set setAcceptedTypes(acceptedTypes: string[]) {
    this.acceptedTypes = acceptedTypes ?? [];
    this.joinedAcceptedTypes = this.acceptedTypes.join(',');
  }

  @Output() readonly filesAdded: EventEmitter<PieceFile[]> = new EventEmitter<PieceFile[]>();

  acceptedTypes: string[] = UPLOAD_FILE_CONFIG.extendedAcceptedFileTypes;
  joinedAcceptedTypes = this.acceptedTypes.join(',');

  constructor(
    private documentApiService: DocumentApiService,
    private addFileFromDocumentsDialogService: AddFileFromDocumentsDialogService,
    private snackbarService: TiimeSnackbarService
  ) {}

  handleNewFiles(files: FileList): void {
    if (files.length === 0) {
      return;
    }

    const acceptedFiles = Array.from(files).filter(file => FileHelper.isValid(file, this.acceptedTypes));

    if (files.length !== acceptedFiles.length) {
      this.snackbarService.openError(FileHelper.getInvalidFileErrorMessage(this.acceptedTypes));
    }

    const result: PieceFile[] = acceptedFiles.map(file => {
      const workbookSheetFile = new PieceFile(null, file.name, file.type, DateHelper.format(new Date()));
      workbookSheetFile.file = file;
      return workbookSheetFile;
    });

    this.fileInput().nativeElement.value = '';
    this.filesAdded.emit(result);
  }

  openAddFilesFromDocumentsDialog(): void {
    this.addFileFromDocumentsDialogService
      .open()
      .afterClosed()
      .pipe(
        filter((document): document is Document => !!document),
        switchMap(document => this.documentApiService.downloadDocument(document.id)),
        tap(fileSaver => {
          const result: PieceFile = new PieceFile(
            null,
            fileSaver.filename,
            fileSaver.file.type,
            DateHelper.format(new Date())
          );
          result.file = new File([fileSaver.file], fileSaver.filename);
          this.filesAdded.emit([result]);
        })
      )
      .subscribe();
  }
}
